import TimeOfDayResolution = App.Data.RuleEngine.Conditions.TimeOfDayResolution;
import RuleSetVersionType = App.Enums.RuleSetVersionType;

/**
 * Plain data model for the rule engine.
 *
 * Rules and conditions are discriminated unions on `type`, not class instances.
 * That keeps this module free of Vue imports (see `ruleTypeRegistry` for the
 * type → component wiring), makes `structuredClone` sufficient for snapshots
 * and undo, and lets TypeScript narrow on `type` instead of `instanceof`.
 */

export type RuleDimension = {
	uuid: string;
	name_clean: string;
	family_name_clean: string;
	is_enabled: boolean;
	supported_condition_types: ConditionType[];
	data_source: string;
	has_predefined_options: boolean;
	available_for_rule_sets: string[] | null;
	ab_test_uuid: string | null;
};

export type ComparisonOperator =
	| "less_than"
	| "less_than_or_equal_to"
	| "equal_to"
	| "not_equal_to"
	| "greater_than"
	| "greater_than_or_equal_to";

export type TimeRangeItem = {
	start: string;
	end: string;
};

export type TimeRangeValue = {
	resolution: TimeOfDayResolution;
	times: TimeRangeItem[];
};

type ConditionBase = {
	uuid: string;
	dimension: RuleDimension;
};

export type InConditionNumber = ConditionBase & {
	type: "in_number";
	values: number[];
};

export type InConditionString = ConditionBase & {
	type: "in_string";
	values: string[];
};

export type NotInConditionNumber = ConditionBase & {
	type: "not_in_number";
	values: number[];
};

export type NotInConditionString = ConditionBase & {
	type: "not_in_string";
	values: string[];
};

export type DayOfWeekCondition = ConditionBase & {
	type: "day_of_week";
	values: number[];
};

export type TimeOfDayCondition = ConditionBase & {
	type: "time_of_day";
	value: TimeRangeValue;
};

export type ComparisonConditionDate = ConditionBase & {
	type: "comparison_date";
	operator: ComparisonOperator;
	value: string;
};

export type ComparisonConditionNumber = ConditionBase & {
	type: "comparison_number";
	operator: ComparisonOperator;
	value: number;
};

export type ComparisonConditionString = ConditionBase & {
	type: "comparison_string";
	operator: "equal_to";
	value: string;
};

export type ComparisonConditionBoolean = ConditionBase & {
	type: "comparison_boolean";
	operator: "equal_to";
	value: boolean;
};

export type BetweenConditionDate = ConditionBase & {
	type: "between_date";
	from: string;
	to: string;
};

export type BetweenConditionNumber = ConditionBase & {
	type: "between_number";
	from: number;
	to: number;
};

export type ABTestCondition = ConditionBase & {
	type: "ab_test";
	values: string[];
};

/**
 * A branch of the explorer's levelled hierarchy tree — a node or a single asset — identified the way the
 * explorer's hierarchy filter identifies it.
 */
export type HierarchyBranch = {
	level: number;
	label: string;
};

/**
 * A selection made in the explorer's hierarchy tree: everything at or under `scope` (the whole tree when
 * null), minus the excluded branches. Labels are per tree, so the definition is part of the selection.
 *
 * `selection_depth` is how many levels below the scope the tree was broken down to; it only restores the
 * editor's view and does not affect matching.
 */
export type HierarchyConditionFields = {
	hierarchy_definition_id: number;
	scope: HierarchyBranch | null;
	selection_depth: number;
	excluded_branches: HierarchyBranch[];
};

/** A selection while it is being made: no tree has been picked until the trees have loaded. */
export type HierarchySelection = Omit<
	HierarchyConditionFields,
	"hierarchy_definition_id"
> & {
	hierarchy_definition_id: number | null;
};

export const emptyHierarchySelection = (
	hierarchyDefinitionId: number | null = null
): HierarchySelection => ({
	hierarchy_definition_id: hierarchyDefinitionId,
	scope: null,
	selection_depth: 1,
	excluded_branches: [],
});

export type InHierarchyCondition = ConditionBase &
	HierarchyConditionFields & {
		type: "in_hierarchy";
	};

export type NotInHierarchyCondition = ConditionBase &
	HierarchyConditionFields & {
		type: "not_in_hierarchy";
	};

/**
 * A condition that has been added but not yet configured. It exists only while
 * the editor is open — a rule cannot be saved while one is present — so the
 * dimension picker can start empty instead of guessing a dimension for the user.
 */
export type DraftCondition = {
	uuid: string;
	dimension: RuleDimension | null;
	/**
	 * A literal rather than `null`: this project compiles with `strict: false`,
	 * so `null` is assignable to every type and cannot discriminate a union.
	 */
	type: "draft";
};

export type Condition =
	| InConditionNumber
	| InConditionString
	| NotInConditionNumber
	| NotInConditionString
	| DayOfWeekCondition
	| TimeOfDayCondition
	| ComparisonConditionDate
	| ComparisonConditionNumber
	| ComparisonConditionString
	| ComparisonConditionBoolean
	| BetweenConditionDate
	| BetweenConditionNumber
	| ABTestCondition
	| InHierarchyCondition
	| NotInHierarchyCondition
	| DraftCondition;

export const createDraftCondition = (uuid: string): DraftCondition => ({
	uuid,
	dimension: null,
	type: "draft",
});

export const isDraftCondition = (
	condition: Condition
): condition is DraftCondition => condition.type === "draft";

/** Only the configured types — a draft has no type to key off. */
export type ConditionType = Exclude<Condition, DraftCondition>["type"];

/**
 * Retained for call sites that key off condition type (the configure dialog's
 * selection-component map, and `keyof ConditionTypeSelectionMap` annotations).
 */
export type ConditionTypeSelectionMap = {
	[K in ConditionType]: Extract<Condition, { type: K }>;
};

/** Condition types whose value is a multi-select list. */
export const MULTI_VALUE_CONDITION_TYPES = [
	"in_number",
	"in_string",
	"not_in_number",
	"not_in_string",
	"day_of_week",
	"ab_test",
] as const satisfies readonly ConditionType[];

export type ActionVariables = "price_per_km" | "price_per_hour"; // load from BE

export type Action =
	| {
			uuid: string;
			type: "modify_percentage" | "modify_absolute" | "set_absolute";
			variable: ActionVariables;
			amount: number;
	  }
	| {
			uuid: string;
			type: "add_task_to_bex_reservation";
			task_id: string;
	  }
	| {
			uuid: string;
			type: "add_label_to_bex_reservation";
			label_id: string;
	  };

export type ConditionRule = {
	uuid: string;
	type: "condition";
	conditions: Condition[];
	actions: Action[];
};

export type RuleSet = {
	uuid: string;
	name: string;
	rules: Rule[];
	version_type: RuleSetVersionType | null;
};

export type RuleSetRule = {
	uuid: string;
	type: "rule_set";
	rule_set: RuleSet;
	conditions?: Condition[];
};

export type LoadMyWheelsBasePriceRule = {
	uuid: string;
	type: "load_my_wheels_base_price";
	conditions?: Condition[];
};

export type ClampVariableRule = {
	uuid: string;
	type: "clamp_variable";
	variable_name: string;
	min_value: number;
	max_value: number;
	conditions?: Condition[];
};

export type Rule =
	| ConditionRule
	| RuleSetRule
	| LoadMyWheelsBasePriceRule
	| ClampVariableRule;

export const isRuleSetRule = (rule: Rule): rule is RuleSetRule =>
	rule.type === "rule_set";

export const createConditionRule = (
	uuid: string,
	conditions: Condition[] = [],
	actions: Action[] = []
): ConditionRule => ({ uuid, type: "condition", conditions, actions });

export const createRuleSet = (
	uuid: string,
	name: string,
	rules: Rule[] = [],
	version_type: RuleSetVersionType | null = null
): RuleSet => ({ uuid, name, rules, version_type });

export const createRuleSetRule = (
	uuid: string,
	rule_set: RuleSet,
	conditions: Condition[] = []
): RuleSetRule => ({ uuid, type: "rule_set", rule_set, conditions });

export const createLoadMyWheelsBasePriceRule = (
	uuid: string,
	conditions: Condition[] = []
): LoadMyWheelsBasePriceRule => ({
	uuid,
	type: "load_my_wheels_base_price",
	conditions,
});

export const createClampVariableRule = (
	uuid: string,
	variable_name: string,
	min_value: number,
	max_value: number,
	conditions: Condition[] = []
): ClampVariableRule => ({
	uuid,
	type: "clamp_variable",
	variable_name,
	min_value,
	max_value,
	conditions,
});
